import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, HostListener, OnChanges, SimpleChanges, ElementRef, ViewChild, AfterViewInit, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ModalConfig, ModalButton } from './interfaces/modal.interface';
import { gsap } from 'gsap';

@Component({
  selector: 'shared-modal',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.css']
})
export class ModalComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @ViewChild('modalBackdrop', { static: false }) modalBackdrop!: ElementRef;
  @ViewChild('modalContainer', { static: false }) modalContainer!: ElementRef;

  private platformId = inject(PLATFORM_ID);

  @Input() isOpen = false;
  @Input() config: ModalConfig = {
    title: '',
    size: 'md',
    showCloseButton: true,
    closeOnBackdrop: true,
    closeOnEscape: true
  };
  @Input() customContent = false;

  @Output() modalClose = new EventEmitter<void>();
  @Output() buttonClick = new EventEmitter<string>();
  @Output() backdropClick = new EventEmitter<void>();

  private modalTimeline: gsap.core.Timeline | null = null;

  ngAfterViewInit(): void {
    // Initialize GSAP timeline
    this.modalTimeline = gsap.timeline({ paused: true });
    this.setupAnimations();

    // If the modal starts open, ensure it animates in after view init
    if (this.isOpen) {
      if (isPlatformBrowser(this.platformId)) {
        document.body.classList.add('modal-open');
      }
      this.animateIn();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent) {
    if (this.isOpen && this.config.closeOnEscape) {
      this.closeModal();
    }
  }

  ngOnInit() {
    if (this.isOpen) {
      if (isPlatformBrowser(this.platformId)) {
        document.body.classList.add('modal-open');
      }
    }
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
      document.body.classList.remove('modal-open');
    }
    if (this.modalTimeline) {
      this.modalTimeline.kill();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isOpen']) {
      if (this.isOpen) {
        if (isPlatformBrowser(this.platformId)) {
          document.body.classList.add('modal-open');
        }
        this.animateIn();
      } else {
        if (isPlatformBrowser(this.platformId)) {
          document.body.classList.remove('modal-open');
        }
        this.animateOut();
      }
    }
  }

  private setupAnimations(): void {
    if (!this.modalBackdrop || !this.modalContainer) return;

    // Set initial states
    gsap.set(this.modalBackdrop.nativeElement, { opacity: 0 });
    gsap.set(this.modalContainer.nativeElement, { 
      opacity: 0, 
      scale: 0.8,
      y: -50
    });
  }

  private animateIn(): void {
    if (!this.modalBackdrop || !this.modalContainer || !this.modalTimeline) return;

    this.modalTimeline.clear();
    this.modalTimeline
      .to(this.modalBackdrop.nativeElement, {
        opacity: 1,
        duration: 0.2,
        ease: "power2.out"
      })
      .to(this.modalContainer.nativeElement, {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.3,
        ease: "back.out(1.7)"
      }, "-=0.1");

    this.modalTimeline.play();
  }

  private animateOut(): void {
    if (!this.modalBackdrop || !this.modalContainer || !this.modalTimeline) return;

    this.modalTimeline.clear();
    this.modalTimeline
      .to(this.modalContainer.nativeElement, {
        opacity: 0,
        scale: 0.8,
        y: -50,
        duration: 0.2,
        ease: "power2.in"
      })
      .to(this.modalBackdrop.nativeElement, {
        opacity: 0,
        duration: 0.15,
        ease: "power2.in"
      }, "-=0.1");

    this.modalTimeline.play();
  }

  closeModal() {
    this.modalClose.emit();
  }

  onBackdropClick(event: Event) {
    if (event.target === event.currentTarget && this.config.closeOnBackdrop) {
      this.backdropClick.emit();
      this.closeModal();
    }
  }

  onButtonClick(action: string) {
    this.buttonClick.emit(action);
  }

  getModalSizeClass(): string {
    const sizeClasses = {
      'sm': 'max-w-sm sm:max-w-md',
      'md': 'max-w-md sm:max-w-lg',
      'lg': 'max-w-lg sm:max-w-xl md:max-w-2xl',
      'xl': 'max-w-xl sm:max-w-2xl md:max-w-3xl lg:max-w-4xl',
      '2xl': 'max-w-2xl sm:max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl'
    };
    return sizeClasses[this.config.size || 'md'];
  }

  getButtonClass(button: ModalButton): string {
    const baseClasses = 'px-4 py-2 rounded-md font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 inline-flex items-center justify-center min-w-[80px]';

    const typeClasses = {
      'primary': 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 shadow-sm hover:shadow-md',
      'secondary': 'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500 shadow-sm hover:shadow-md',
      'danger': 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 shadow-sm hover:shadow-md',
      'success': 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 shadow-sm hover:shadow-md',
      'warning': 'bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500 shadow-sm hover:shadow-md',
      'info': 'bg-cyan-600 text-white hover:bg-cyan-700 focus:ring-cyan-500 shadow-sm hover:shadow-md'
    };

    const disabledClasses = 'opacity-50 cursor-not-allowed hover:shadow-none';

    let classes = `${baseClasses} ${typeClasses[button.type]}`;

    if (button.disabled || button.loading) {
      classes += ` ${disabledClasses}`;
    }

    return classes;
  }
}
